import * as React from "react";

import { LockOutlined } from "@ant-design/icons";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { isUnauthenticated } from "@impero-com/common/utils/auth";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Button, InformationModal, Modal } from "@impero-com/ui";
import { useMutation } from "@tanstack/react-query";
import { Input, Form, Spin, FormInstance } from "antd";
import axios from "axios";

import { PostSetupData, SetupData, ValidatePinData } from "api/generated/model";
import {
    postSetup2fa,
    postValidatePin,
    useGetSetup2fa,
} from "api/generated/two-factor/two-factor";

import { DisplayRecoveryCodeTitle } from "../../components/twoFactor/displayRecovery";
import {
    PinCodeForm,
    PinCodeValidationResult,
} from "../../components/twoFactor/pinCodeV4";
import { ENVIRONMENT } from "../../env";

import * as theme from "./theme.css";

interface ForgotRecoveryCodeProps {
    onCloseDialog: () => void;
}

interface ChangeTwoFA {
    type: "CHANGE_TWO_FACTOR";
}
interface PinValidation {
    type: "PIN_VALIDATION";
}
interface DisplayNewRecoveryCode {
    type: "DISPLAYING_NEW_RECOVERY_CODE";
    newRecoveryCode: string;
}
type ForgotRecoveryCodeState =
    | ChangeTwoFA
    | PinValidation
    | DisplayNewRecoveryCode;

const ForgotRecoveryCode: React.FC<ForgotRecoveryCodeProps> = (
    props: ForgotRecoveryCodeProps
) => {
    const [status, setStatus] = React.useState<ForgotRecoveryCodeState>({
        type: "CHANGE_TWO_FACTOR",
    });
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    const [changeTwoFAStepForm] = Form.useForm<ChangeTwoFAStepFormValues>();

    const goToPinValidation = () => {
        setStatus({ type: "PIN_VALIDATION" });
    };

    const goToShowRecoveryCode = (newRecoveryCode: string) => {
        setStatus({ type: "DISPLAYING_NEW_RECOVERY_CODE", newRecoveryCode });
    };

    const renderState = (): React.ReactNode => {
        switch (status.type) {
            case "CHANGE_TWO_FACTOR":
                return (
                    <ChangeTwoFAStep
                        goToNextStep={goToPinValidation}
                        form={changeTwoFAStepForm}
                        markLoading={(loading) => {
                            setIsLoading(loading);
                        }}
                    />
                );
            case "PIN_VALIDATION":
                return (
                    <AwaitingPinValidationStep
                        goToNextStep={goToShowRecoveryCode}
                    />
                );
            case "DISPLAYING_NEW_RECOVERY_CODE":
                return (
                    <DisplayRecoveryCodeStep
                        recoveryCode={status.newRecoveryCode}
                    />
                );
            default:
                return assertNever(status);
        }
    };

    const onClickOk = () => {
        switch (status.type) {
            case "CHANGE_TWO_FACTOR":
                changeTwoFAStepForm.submit();
                break;
            case "PIN_VALIDATION":
                // See the disabled confirm button below
                throw new Error("OK button should be disabled in that state");
            case "DISPLAYING_NEW_RECOVERY_CODE":
                props.onCloseDialog();
                break;
            default:
                assertNever(status);
        }
    };

    // The last step has nothing left to decide, so it acknowledges instead.
    if (status.type === "DISPLAYING_NEW_RECOVERY_CODE") {
        return (
            <InformationModal
                open
                type="success"
                title={t("User_Profile_RecoveryCodeCreated_Title")}
                onClose={props.onCloseDialog}
                footer={
                    <Button variant="primary" onClick={props.onCloseDialog}>
                        {t("Close")}
                    </Button>
                }
            >
                {renderState()}
            </InformationModal>
        );
    }

    return (
        <Modal
            variant="content"
            open
            title={getTitle(status)}
            onClose={props.onCloseDialog}
            footer={
                <>
                    <Button onClick={props.onCloseDialog}>{t("Cancel")}</Button>
                    <Button
                        variant="primary"
                        loading={isLoading}
                        disabled={status.type === "PIN_VALIDATION"}
                        onClick={onClickOk}
                    >
                        {getOkText(status)}
                    </Button>
                </>
            }
        >
            {renderState()}
        </Modal>
    );
};

function getTitle(status: ForgotRecoveryCodeState): string {
    switch (status.type) {
        case "CHANGE_TWO_FACTOR":
            return t("User_Profile_ILostMyRecoveryCode_Title");
        case "PIN_VALIDATION":
            return t("User_Profile_PinValidation_Title");
        case "DISPLAYING_NEW_RECOVERY_CODE":
            return t("User_Profile_RecoveryCodeCreated_Title");
        default:
            return assertNever(status);
    }
}

function getOkText(status: ForgotRecoveryCodeState): string {
    switch (status.type) {
        case "CHANGE_TWO_FACTOR":
            return t("User_Profile_GetNewRecoveryCode");
        case "PIN_VALIDATION":
            return t("User_Profile_PinValidation");
        case "DISPLAYING_NEW_RECOVERY_CODE":
            return t("Close");
        default:
            return assertNever(status);
    }
}

interface ChangeTwoFactorSuccess {
    type: "SUCCESS";
    setupData: SetupData;
}
interface ChangeTwoFactorError {
    type: "ERROR";
    status: number;
    requiresHumanVerification: boolean;
}
type ChangeTwoFactorResult = ChangeTwoFactorSuccess | ChangeTwoFactorError;

async function submitTwoFactorChange(
    setupData: PostSetupData
): Promise<ChangeTwoFactorResult> {
    try {
        const res = await postSetup2fa(setupData);
        return { type: "SUCCESS" as const, setupData: res.data };
    } catch (e) {
        if (!axios.isAxiosError(e) || !e.response) {
            throw e;
        }
        const { status, data } = e.response;
        return {
            type: "ERROR" as const,
            status,
            requiresHumanVerification:
                isUnauthenticated(data) &&
                data.status === "notAuthenticated" &&
                data.requiresHumanVerification,
        };
    }
}

// Step 1 : Ready to unverify the phone number

interface ChangeTwoFAStepFormValues {
    password: string;
    captcha: string | null;
}

interface ChangeTwoFAStepProps {
    goToNextStep: () => void;
    form: FormInstance<ChangeTwoFAStepFormValues>;
    markLoading: (loading: boolean) => void;
}

const ChangeTwoFAStep: React.FC<ChangeTwoFAStepProps> = (
    props: ChangeTwoFAStepProps
) => {
    const [wrongPassword, setWrongPassword] = React.useState<boolean>(false);
    const [requiresCaptcha, setRequiresCaptcha] =
        React.useState<boolean>(false);
    const captchaRef = React.useRef<HCaptcha>(null);
    const {
        isSuccess,
        data,
        status,
        isPending: queryPending,
    } = useGetSetup2fa({
        query: {
            select: (response) => response.data,
        },
    });

    const { mutate, isPending: mutationPending } = useMutation<
        ChangeTwoFactorResult,
        unknown,
        PostSetupData
    >({
        mutationFn: submitTwoFactorChange,
        onSuccess: (result: ChangeTwoFactorResult) => {
            switch (result.type) {
                case "SUCCESS":
                    props.markLoading(false);
                    props.goToNextStep();
                    break;
                case "ERROR":
                    if (result.status !== 401) {
                        throw new Error(
                            "Unhandled error code : " + result.status
                        );
                    }

                    if (result.requiresHumanVerification) {
                        setRequiresCaptcha(true);
                    }
                    setWrongPassword(true);
                    break;
            }
        },
    });

    const onSubmitTwoFa = (values: ChangeTwoFAStepFormValues) => {
        if (!isSuccess) {
            // We do this so that we now `data` is set
            throw new Error("Submitting a form that is not in the DOM");
        }

        if (data.phoneNumber === null || data.phoneNumber === undefined) {
            throw new Error("Phone number has to be set in the context");
        }

        const { password, captcha } = values;
        const setupData: PostSetupData = {
            prefix: data.phoneNumber.prefix,
            number: data.phoneNumber.number,
            password,
            challenge: null,
            captcha,
        };
        mutate(setupData);

        // Captcha are one-time-usage
        if (captchaRef.current !== null) {
            captchaRef.current.resetCaptcha();
        }
    };

    const handleCaptchaVerify = (token: string) => {
        props.form.setFieldValue("captcha", token);
    };

    const { markLoading } = props;
    React.useEffect(() => {
        markLoading(queryPending || mutationPending);
    }, [queryPending, mutationPending, markLoading]);

    switch (status) {
        case "pending":
            return <Spin />;
        case "error":
            // oops: couldn't load the two FA setup
            throw new Error(
                "Failed to load two FA setup : this should not happen on a logged-in page"
            );
        case "success":
            const validateStatus = wrongPassword ? "error" : "";
            const validationHelp = wrongPassword
                ? t("User_Profile_WrongPassword")
                : null;
            return (
                <Form form={props.form} onFinish={onSubmitTwoFa}>
                    <p>{t("User_Profile_GetNewRecoveryCodeText")}</p>
                    <Form.Item
                        name="password"
                        validateStatus={validateStatus}
                        help={validationHelp}
                        rules={[{ required: true }]}
                    >
                        <Input
                            prefix={
                                <LockOutlined className={theme.iconOpacity} />
                            }
                            type="password"
                            placeholder={t("Login_Password")}
                        />
                    </Form.Item>
                    {requiresCaptcha && (
                        <Form.Item name="captcha" rules={[{ required: true }]}>
                            <HCaptcha
                                size="normal"
                                sitekey={ENVIRONMENT.IMPERO_HCAPTCHA_SITE_KEY}
                                onVerify={handleCaptchaVerify}
                                ref={captchaRef}
                            />
                        </Form.Item>
                    )}
                </Form>
            );
        default:
            return assertNever(status);
    }
};

// Step 2 : Awaiting the pin code that was sent to the user

interface ValidatePinSuccess {
    type: "SUCCESS";
    newRecoveryCode: string;
}
interface ValidatePinError {
    type: "ERROR";
    requiresHumanVerification: boolean;
}
type ValidatePinResponse = ValidatePinSuccess | ValidatePinError;

async function submitValidatePin(
    pinData: ValidatePinData
): Promise<ValidatePinResponse> {
    let newRecoveryCode;
    try {
        newRecoveryCode = (await postValidatePin(pinData)).data.recoveryCode;
    } catch (e) {
        const data = axios.isAxiosError(e) ? e.response?.data : undefined;
        return {
            type: "ERROR" as const,
            requiresHumanVerification:
                isUnauthenticated(data) &&
                data.status === "notAuthenticated" &&
                data.requiresHumanVerification,
        };
    }
    if (newRecoveryCode === null || newRecoveryCode === undefined) {
        throw new Error(
            "Verifying a phone number should produce a new recovery code"
        );
    }
    return { type: "SUCCESS" as const, newRecoveryCode };
}

interface AwaitingPinValidationStepProps {
    goToNextStep: (newRecoveryCode: string) => void;
}

function AwaitingPinValidationStep(props: AwaitingPinValidationStepProps) {
    const { mutateAsync } = useMutation<
        ValidatePinResponse,
        unknown,
        ValidatePinData
    >({
        mutationFn: submitValidatePin,
        onSuccess: (response: ValidatePinResponse) => {
            switch (response.type) {
                case "SUCCESS":
                    props.goToNextStep(response.newRecoveryCode);
                    break;
                case "ERROR":
                    break;
                default:
                    assertNever(response);
            }
        },
    });

    const onSubmitPin = async (
        pin: string,
        captcha: string | null
    ): Promise<PinCodeValidationResult> => {
        const validatePinData: ValidatePinData = {
            pin,
            challenge: null,
            human_verification_response: captcha,
        };
        const data = await mutateAsync(validatePinData);

        switch (data.type) {
            case "SUCCESS":
                return { type: "SUCCESS" };
            case "ERROR":
                return {
                    type: "ERROR",
                    error: t("Login_IncorrectOrExpiredPin"),
                    requiresHumanVerification: data.requiresHumanVerification,
                };
            default:
                return assertNever(data);
        }
    };

    return (
        <>
            <p>{t("User_Profile_VerifyPhoneNumber")}</p>
            <PinCodeForm onSubmit={onSubmitPin} />
        </>
    );
}

// Step 3 : Displayng the new recovery code

interface DisplayRecoveryCodeStepProps {
    recoveryCode: string;
}

const DisplayRecoveryCodeStep: React.FC<DisplayRecoveryCodeStepProps> = (
    props: DisplayRecoveryCodeStepProps
) => {
    return (
        <div className={theme.recoveryCodeContainer}>
            <p>{t("User_Profile_NewRecoveryCode")}</p>
            <DisplayRecoveryCodeTitle recoveryCode={props.recoveryCode} />
        </div>
    );
};

export { ForgotRecoveryCode };
